import { ApiError } from '../../services/api';
import {
  OfferDiscountType,
  OfferItemView,
  OfferRejectionReasonCode,
  OfferResponse,
  OfferStatus,
  REJECTION_REASONS,
  SUSPENDABLE_STATUSES,
} from '../../types/offers';

/** BR-10 / suspend: the reason the partner reads must be at least 10 characters. */
export const MIN_REASON_LENGTH = 10;

export const STATE_CHANGED_MESSAGE = 'This offer changed, review again';

export const STATUS_LABELS: Record<OfferStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending approval',
  SCHEDULED: 'Scheduled',
  LIVE: 'Live',
  PAUSED: 'Paused',
  ENDED: 'Ended',
  REJECTED: 'Rejected',
  SUSPENDED: 'Suspended',
  SUPERSEDED: 'Superseded',
};

/** `admin-badge` modifier per status (base colours in admin.css, extras in orders.css). */
export const STATUS_BADGE: Record<OfferStatus, string> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'pending',
  SCHEDULED: 'order-info',
  LIVE: 'approved',
  PAUSED: 'warning',
  ENDED: 'neutral',
  REJECTED: 'rejected',
  SUSPENDED: 'rejected',
  SUPERSEDED: 'neutral',
};

export function isSuspendable(status: OfferStatus): boolean {
  return SUSPENDABLE_STATUSES.includes(status);
}

export function formatAed(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  return `AED ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/** `YYYY-MM-DD` (Asia/Dubai LocalDate) → "6 Oct 2026", parsed as a calendar date (no TZ shift). */
export function formatDay(day: string | null | undefined): string {
  if (!day) return '—';
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return day;
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatWindow(startsAt: string, endsAt: string): string {
  return `${formatDay(startsAt)} – ${formatDay(endsAt)}`;
}

export function formatInstant(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** "10%" / "AED 30" — one discount value of the given type. */
export function discountValueText(type: OfferDiscountType, value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return type === 'PERCENT' ? `${Number(value)}%` : formatAed(value);
}

/**
 * "15% OFF" / "AED 30 OFF" / "Up to 30% OFF" — the backend label when present (Spec 073: it already
 * says "Up to …" when the services' values differ), else computed the same way.
 */
export function discountLabel(
  o: Pick<OfferResponse, 'label' | 'discountType' | 'discountValue' | 'discountVaries' | 'maxDiscountValue'>,
): string {
  if (o.label) return o.label;
  if (o.discountVaries && o.maxDiscountValue != null) return `Up to ${discountValueText(o.discountType, o.maxDiscountValue)} OFF`;
  if (o.discountValue == null) return '—';
  return `${discountValueText(o.discountType, o.discountValue)} OFF`;
}

/** Spec 073: one service's own discount ("10%" / "AED 20"); "—" when the row has no value. */
export function itemDiscountText(
  item: Pick<OfferItemView, 'discountValue'>,
  o: Pick<OfferResponse, 'discountType'>,
): string {
  return discountValueText(o.discountType, item.discountValue);
}

export function reasonLabel(code: OfferRejectionReasonCode | '' | null | undefined): string {
  return REJECTION_REASONS.find((r) => r.code === code)?.label ?? '';
}

/**
 * The text the server measures for BR-10: picker label, then a space and the trimmed free text
 * when there is any (garages `AdminOfferService.reject`). Mirrored so the button enables exactly
 * when the server would accept.
 */
export function combinedRejectReason(code: OfferRejectionReasonCode | '', text: string): string {
  const label = reasonLabel(code);
  if (!label) return '';
  const trimmed = text.trim();
  return trimmed ? `${label} ${trimmed}` : label;
}

export function isRejectValid(code: OfferRejectionReasonCode | '', text: string): boolean {
  return code !== '' && combinedRejectReason(code, text).length >= MIN_REASON_LENGTH;
}

export function isSuspendValid(text: string): boolean {
  return text.trim().length >= MIN_REASON_LENGTH;
}

/** P4 hint: words that are only fine when the offer really delivers them. */
const CLAIM_PATTERNS: ReadonlyArray<RegExp> = [
  /\bfree\b/i,
  /\bguarantee(d)?\b/i,
  /\b100\s*%/i,
  /\bbest\b/i,
  /\bcheapest\b/i,
  /\bunlimited\b/i,
  /مجان/,
  /مضمون/,
  /ضمان/,
  /الأفضل/,
  /الأرخص/,
];

export function misleadingClaimHits(o: Pick<OfferResponse, 'titleEn' | 'titleAr' | 'subtitleEn' | 'subtitleAr'>): string[] {
  const text = [o.titleEn, o.titleAr, o.subtitleEn, o.subtitleAr].filter(Boolean).join(' ');
  const hits = new Set<string>();
  for (const pattern of CLAIM_PATTERNS) {
    const match = text.match(pattern);
    if (match) hits.add(match[0].trim());
  }
  return [...hits];
}

export interface DecisionError {
  message: string;
  /** True when the page must reload the offer before another decision (stale version / status). */
  reload: boolean;
}

const PAST = { approve: 'approved', reject: 'rejected', suspend: 'suspended' } as const;

/** Maps the design §3 error codes to what the admin reads. */
export function decisionError(err: unknown, action: 'approve' | 'reject' | 'suspend'): DecisionError {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'OFFER_STATE_CHANGED':
        return { message: STATE_CHANGED_MESSAGE, reload: true };
      case 'INVALID_OFFER_TRANSITION':
        return { message: err.message || `This offer can no longer be ${PAST[action]}.`, reload: true };
      case 'OFFER_NOT_APPROVABLE':
        return {
          message: err.message?.startsWith("Can't approve") ? err.message : `Can't approve: ${err.message}`,
          reload: false,
        };
      case 'REASON_TOO_SHORT':
        return { message: 'Give the partner a reason (at least 10 characters)', reload: false };
      case 'FEATURE_DISABLED':
        return { message: 'Offers are temporarily disabled', reload: false };
      default:
        break;
    }
    if (err.status === 403) return { message: 'You do not have access to review offers.', reload: false };
    if (err.status === 404) return { message: 'This offer no longer exists.', reload: false };
  }
  return { message: err instanceof Error ? err.message : `Could not ${action} the offer.`, reload: false };
}
